import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api/api';
import { useAuthenticated } from '@gettactic/hooks';
import { RequestStatus } from '../v2/pages/settings/WorkplaceRequestsPage/workplace-requests-settings/types';
import { WorkplaceRequestStatus, CreateWorkplaceRequestParams } from '@gettactic/api';

type UpdateWorkplaceRequestParams = {
  description?: string | null;
  update_comment?: string | null;
  type_id?: string | null;
};

type UpdateMultipleWorkplaceRequestParams = {
  title?: string | null;
  description?: string | null;
  type_id?: string | null;
  office_id?: string | null;
  schedule_id?: string | null;
};

type BatchResult<T> = {
  successful: Array<{ requestId: string; result: T }>;
  failed: Array<{ requestId: string; error: any }>;
  totalRequested: number;
  totalSuccessful: number;
  totalFailed: number;
};

export function useWorkplaceRequestMutations() {
  const queryClient = useQueryClient();
  const {
    userContext: { deviceTz, authenticatedUser }
  } = useAuthenticated();
  const tz = authenticatedUser?.user?.time_zone;

  const updateAll = useCallback(() => {
    queryClient.invalidateQueries(['workplaceRequests']);
    queryClient.invalidateQueries(['workplaceRequestsBySchedule']);
  }, [queryClient]);

  const showBatchToast = useCallback((
    operation: string,
    totalSuccessful: number,
    totalFailed: number,
    totalRequested: number
  ) => {
    if (totalFailed === 0) {
      if (totalSuccessful === 1) {
        toast.success(`${operation} successfully`);
      } else {
        toast.success(`${totalSuccessful} ${operation}s successfully`);
      }
    } else if (totalSuccessful === 0) {
      toast.error(`Failed to ${operation.toLowerCase()} ${totalFailed} requests`);
    } else {
      toast.success(
        `${operation} ${totalSuccessful} of ${totalRequested} requests successfully. ${totalFailed} failed.`,
        { duration: 4000 }
      );
    }
  }, []);

  const processBatchResults = useCallback(<T>(
    results: PromiseSettledResult<{ success: boolean; requestId: string; result?: T; error?: any }>[]
  ): BatchResult<T> => {
    const successful = results
      .filter((result): result is PromiseFulfilledResult<{ success: true; requestId: string; result: T }> => 
        result.status === "fulfilled" && result.value.success
      )
      .map(result => ({ requestId: result.value.requestId, result: result.value.result! }));
    
    const failed = results
      .filter((result): result is PromiseFulfilledResult<{ success: false; requestId: string; error: any }> => 
        result.status === "fulfilled" && !result.value.success
      )
      .map(result => ({ requestId: result.value.requestId, error: result.value.error }));
    
    return {
      successful,
      failed,
      totalRequested: results.length,
      totalSuccessful: successful.length,
      totalFailed: failed.length
    };
  }, []);

  const updateWorkplaceRequestStatusMutation = useMutation(
    async ({ requestId, status, comment }: { requestId: string; status: RequestStatus; comment?: string }) => {
      const updates: {
        status: WorkplaceRequestStatus;
        update_comment?: string;
      } = {
        status: status.toLowerCase() as WorkplaceRequestStatus
      };

      if (comment?.trim()) {
        updates.update_comment = comment.trim();
      }

      return await api.client.workplaceRequests.updateWorkplaceRequestStatus(
        requestId,
        status.toLowerCase() as WorkplaceRequestStatus,
        comment,
        tz ?? deviceTz
      );
    },
    {
      onSuccess: () => {
        updateAll();
        toast.success('Request status updated successfully');
      },
      onError: (error) => {
        console.error('Error updating workplace request status:', error);
        toast.error('Failed to update request status');
      }
    }
  );

  const updateWorkplaceRequestStatus = useCallback(
    (requestId: string, status: RequestStatus, comment?: string) => {
      return updateWorkplaceRequestStatusMutation.mutateAsync({ requestId, status, comment });
    },
    [updateWorkplaceRequestStatusMutation]
  );

  const updateWorkplaceRequestOwnerMutation = useMutation(
    async ({ requestId, ownerUserId }: { requestId: string; ownerUserId: string | null }) => {
      return await api.client.workplaceRequests.updateWorkplaceRequestOwner(
        requestId,
        ownerUserId,
        tz ?? deviceTz
      );
    },
    {
      onSuccess: () => {
        updateAll();
        toast.success('Request owner updated successfully');
      },
      onError: (error) => {
        console.error('Error updating workplace request owner:', error);
        toast.error('Failed to update request owner');
      }
    }
  );

  const updateWorkplaceRequestOwner = useCallback(
    (requestId: string, ownerUserId: string | null) => {
      return updateWorkplaceRequestOwnerMutation.mutateAsync({ requestId, ownerUserId });
    },
    [updateWorkplaceRequestOwnerMutation]
  );

  const deleteWorkplaceRequestMutation = useMutation(
    async ({ requestId }: { requestId: string }) => {
      return await api.client.workplaceRequests.deleteWorkplaceRequest(requestId);
    },
    {
      onSuccess: () => {
        updateAll();
        toast.success('Request deleted successfully');
      },
      onError: (error) => {
        console.error('Error deleting workplace request:', error);
        toast.error('Failed to delete request');
      }
    }
  );

  const deleteWorkplaceRequest = useCallback(
    (requestId: string) => {
      return deleteWorkplaceRequestMutation.mutateAsync({ requestId });
    },
    [deleteWorkplaceRequestMutation]
  );

  const updateWorkplaceRequestMutation = useMutation(
    async ({ requestId, updates }: { requestId: string; updates: UpdateWorkplaceRequestParams }) => {
      return await api.client.workplaceRequests.updateWorkplaceRequest(
        requestId,
        updates,
        tz ?? deviceTz
      );
    },
    {
      onSuccess: () => {
        updateAll();
        toast.success('Request updated successfully');
      },
      onError: (error) => {
        console.error('Error updating workplace request:', error);
        toast.error('Failed to update request');
      }
    }
  );

  const updateWorkplaceRequest = useCallback(
    (requestId: string, updates: UpdateWorkplaceRequestParams) => {
      return updateWorkplaceRequestMutation.mutateAsync({ requestId, updates });
    },
    [updateWorkplaceRequestMutation]
  );

  const updateMultipleWorkplaceRequestsMutation = useMutation(
    async (requests: Array<{ requestId: string; updates: UpdateMultipleWorkplaceRequestParams }>) => {
      const results = await Promise.allSettled(
        requests.map(({ requestId, updates }) =>
          api.client.workplaceRequests.updateWorkplaceRequest(requestId, updates, tz ?? deviceTz)
            .then(result => ({ success: true, requestId, result }))
            .catch(error => ({ success: false, requestId, error }))
        )
      );
      
      return processBatchResults(results);
    },
    {
      onSuccess: (results) => {
        const { totalSuccessful, totalFailed, totalRequested } = results;
        updateAll();
        showBatchToast('Workplace request updated', totalSuccessful, totalFailed, totalRequested);
      },
      onError: (error) => {
        toast.error("Failed to process workplace request updates");
        console.error("Update multiple workplace requests error:", error);
      }
    }
  );

  const updateMultipleWorkplaceRequests = useCallback(
    (requests: Array<{ requestId: string; updates: UpdateMultipleWorkplaceRequestParams }>) => {
      return updateMultipleWorkplaceRequestsMutation.mutateAsync(requests);
    },
    [updateMultipleWorkplaceRequestsMutation]
  );

  const createWorkplaceRequestMutation = useMutation(
    async (request: CreateWorkplaceRequestParams) => {
      const requestWithTz = {
        ...request,
        tz: tz ?? deviceTz
      };
      const res = await api.client.workplaceRequests.createWorkplaceRequest(requestWithTz);
      return res.result;
    },
    {
      onSuccess: () => {
        updateAll();
        toast.success('Workplace request created successfully');
      },
      onError: (error) => {
        toast.error('Failed to create workplace request');
        console.error('Create workplace request error:', error);
      }
    }
  );

  const createWorkplaceRequest = useCallback(
    (request: CreateWorkplaceRequestParams) => {
      return createWorkplaceRequestMutation.mutateAsync(request);
    },
    [createWorkplaceRequestMutation]
  );

  const createMultipleWorkplaceRequestsMutation = useMutation(
    async (requests: CreateWorkplaceRequestParams[]) => {
      const requestsWithTz = requests.map(request => ({
        ...request,
        tz: tz ?? deviceTz
      }));
      
      // Create all requests in parallel with individual error handling
      const results = await Promise.allSettled(
        requestsWithTz.map((request, index) =>
          api.client.workplaceRequests.createWorkplaceRequest(request)
            .then(res => ({ success: true, requestId: `index-${index}`, result: res.result }))
            .catch(error => ({ success: false, requestId: `index-${index}`, error }))
        )
      );
      
      return processBatchResults(results);
    },
    {
      onSuccess: (results) => {
        updateAll();
        const { totalSuccessful, totalFailed, totalRequested } = results;
        showBatchToast('Workplace request created', totalSuccessful, totalFailed, totalRequested);
      },
      onError: (error) => {
        toast.error('Failed to process workplace requests');
        console.error('Create multiple workplace requests error:', error);
      }
    }
  );

  const createMultipleWorkplaceRequests = useCallback(
    (requests: CreateWorkplaceRequestParams[]) => {
      return createMultipleWorkplaceRequestsMutation.mutateAsync(requests);
    },
    [createMultipleWorkplaceRequestsMutation]
  );

  // Memoized loading state
  const isLoading = useMemo(() => 
    updateWorkplaceRequestStatusMutation.isLoading || 
    updateWorkplaceRequestOwnerMutation.isLoading || 
    deleteWorkplaceRequestMutation.isLoading || 
    updateWorkplaceRequestMutation.isLoading || 
    updateMultipleWorkplaceRequestsMutation.isLoading || 
    createWorkplaceRequestMutation.isLoading || 
    createMultipleWorkplaceRequestsMutation.isLoading,
    [
      updateWorkplaceRequestStatusMutation.isLoading,
      updateWorkplaceRequestOwnerMutation.isLoading,
      deleteWorkplaceRequestMutation.isLoading,
      updateWorkplaceRequestMutation.isLoading,
      updateMultipleWorkplaceRequestsMutation.isLoading,
      createWorkplaceRequestMutation.isLoading,
      createMultipleWorkplaceRequestsMutation.isLoading
    ]
  );

  return {
    updateWorkplaceRequestStatus,
    updateWorkplaceRequestStatusMutation,
    updateWorkplaceRequestOwner,
    updateWorkplaceRequestOwnerMutation,
    deleteWorkplaceRequest,
    deleteWorkplaceRequestMutation,
    updateWorkplaceRequest,
    updateWorkplaceRequestMutation,
    updateMultipleWorkplaceRequests,
    updateMultipleWorkplaceRequestsMutation,
    createWorkplaceRequest,
    createWorkplaceRequestMutation,
    createMultipleWorkplaceRequests,
    createMultipleWorkplaceRequestsMutation,
    isLoading
  };
}
